import SidePanel from '../SidePanel.jsx';
import PaymentForm from '../payments/PaymentForm.jsx';
import { formatDateTime } from '../../lib/DateTime.js';
import { useEffect, useState } from 'react';
import MoneyInput from '../MoneyInput.jsx';
import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import Modal from '../Modal';
import Select from '../Select';
import { useDebounce } from '@uidotdev/usehooks';
import ErrorMessage from '../ErrorMessage';
import Loading from '../Loading';
import PaymentWithProcessor from './PaymentWithProcessor.jsx';
import PaymentWithManualEntry from './PaymentWithManualEntry.jsx';
import SectionPicker from '../SectionPicker.jsx';
import { useBusinessInfo } from '../../lib/Queries';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '../../lib/useBusinessNavigate.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import FeatureGateMessage from '../FeatureGateMessage.jsx';
import {FEATURE_GIFT_CERTIFICATES, useFeatures} from "~/lib/Features";
import PaymentWithGiftCertificate from "~/components/payments/PaymentWithGiftCertificate";

const MANUAL_ENTRY = 'manual';
const VIA_PROCESSOR = 'processor';
const GIFT_CERTIFICATE = 'giftcertificate';

const AdminPaymentPanel = ({
    isOpen,
    onSave,
    onClose,
    objectId,
    objectType,
    totalPrice,
    totalPaid,
}) => {
    const [amount, setAmount] = useState(
        Math.max(0, totalPrice - totalPaid) || '0.00',
    );
    const [method, setMethod] = useState(MANUAL_ENTRY);
    const { isEnabled } = useFeatures();

    const { data: businessInfo } = useBusinessInfo();
    const canPay = !!businessInfo?.canPay;

    useEffect(() => {
        if (!isOpen) {
            setMethod(MANUAL_ENTRY);
        }
    }, [isOpen]);

    const hasAmount = parseFloat(amount) > 0;

    let paymentOptions = [
        { value: MANUAL_ENTRY, label: 'Manual' },
        { value: VIA_PROCESSOR, label: 'Credit Card' },
    ];

    if (isEnabled(FEATURE_GIFT_CERTIFICATES)) {
        paymentOptions.push({ value: GIFT_CERTIFICATE, label: 'Gift Certificate' })
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Make a Payment">
            <h2 className="mb-6">Make a Payment</h2>
            <MoneyInput
                label="Amount"
                value={amount}
                onChange={setAmount}
                className="mb-4"
            />

            <FeatureGateMessage className="my-4">
                You are currently on a Free plan, payments are available on paid
                plans only
            </FeatureGateMessage>

            <SectionPicker
                value={method}
                onChange={setMethod}
                options={paymentOptions}
                slim={true}
                className="flex mb-4"
            />

            {!!method && !hasAmount && (
                <ErrorMessage>
                    Please choose an amount &gt;{formatCurrency(0)} to pay
                </ErrorMessage>
            )}
            {hasAmount && (
                <>
                    {method === MANUAL_ENTRY && (
                        <PaymentWithManualEntry
                            objectId={objectId}
                            objectType={objectType}
                            amount={amount}
                            onSave={() => {
                                if (typeof onSave === 'function') onSave();
                                onClose();
                            }}
                            totalPrice={totalPrice}
                            totalPaid={totalPaid}
                        />
                    )}
                    {method === VIA_PROCESSOR && canPay && (
                        <PaymentWithProcessor
                            objectId={objectId}
                            objectType={objectType}
                            amount={amount}
                            onClose={onClose}
                        />
                    )}
                    {method === GIFT_CERTIFICATE && (
                        <PaymentWithGiftCertificate
                            objectId={objectId}
                            objectType={objectType}
                            amount={amount}
                            onSave={() => {
                                if (typeof onSave === 'function') onSave();
                                onClose();
                            }}
                            totalPrice={totalPrice}
                            totalPaid={totalPaid}
                        />
                    )}
                    {method === VIA_PROCESSOR && !canPay && (
                        <InfoMessageBlock>
                            Please set up a payment processor{' '}
                            <Link to={getBusinessUrl('/integrations/payments')}>
                                under Integrations
                            </Link>{' '}
                            to accept payments
                        </InfoMessageBlock>
                    )}
                </>
            )}
        </Modal>
    );
};

export default AdminPaymentPanel;
